<i18n locale="nl" lang="json">
{
  "close": "Sluiten"
}
</i18n>
<i18n locale="en" lang="json">
{
  "close": "Close"
}
</i18n>

<template>
  <div>
    <the-dialog-loader
      :is-open="!disableDialogLoader && loading && isOpen"
      @click:outside="cancelWhenNotPersistent('click:outside')"
    />
    <v-dialog
      v-if="disableDialogLoader || !loading"
      ref="vDialogRef"
      :model-value="internalIsOpen && (disableDialogLoader || !loading)"
      :fullscreen="computedFullscreen"
      persistent
      :max-width="computedFullscreen ? undefined : width"
      scrollable
      :retain-focus="false"
      content-class="base-dialog"
      :aria-label="$attrs['aria-label']"
      @update:model-value="internalIsOpen = $event"
      @click:outside="cancelWhenNotPersistent('click:outside')"
      @keydown.esc="cancelWhenNotPersistent()"
    >
      <base-card
        v-if="internalIsOpen"
        ref="card"
        :test-id="[componentName, testId]"
        tabindex="0"
        :loading="loading"
        :actions-class="actionsClass"
      >
        <template #title-section>
          <v-card-title class="pa-0">
            <v-toolbar
              dark
              :density="
                !computedFullscreen || display.xs ? 'compact' : 'default'
              "
              flat
              color="primary"
            >
              <v-btn
                v-if="cancelable && computedFullscreen"
                v-set-test-id="[componentName, 'FullscreenCloseButton', testId]"
                icon
                @click="cancel"
              >
                <font-awesome-icon :icon="faArrowLeft" size="lg" />
              </v-btn>
              <v-toolbar-title class="flex-grow-1">
                <slot name="title" v-bind="{ title }">{{ title }}</slot>
              </v-toolbar-title>
              <v-btn
                v-if="cancelable && !computedFullscreen"
                v-set-test-id="[componentName, 'top-close-button', testId]"
                icon
                variant="tonal"
                :aria-label="t('close')"
                @click="cancel"
              >
                <font-awesome-icon :icon="faTimes" size="lg" />
              </v-btn>
            </v-toolbar>
          </v-card-title>
        </template>
        <template #content-section>
          <v-card-text :class="contentClass">
            <slot
              name="default"
              v-bind="{ isOpen: internalIsOpen, resolve, cancel }"
            >
              <p v-if="content" class="font-weight-bold">{{ content }}</p>
            </slot>
          </v-card-text>
        </template>
        <template v-if="slots.actions || buttons?.length" #actions>
          <slot
            name="actions"
            v-bind="{ isOpen: internalIsOpen, resolve, cancel }"
          >
            <base-button
              v-for="(
                { title: buttonTitle, value, props: buttonProps = {}, on = {} },
                index
              ) in buttons || []"
              :key="index"
              v-bind="buttonProps"
              :test-id="[componentName, 'action-button', index]"
              v-on="on"
              @click="resolve(value)"
            >
              {{ buttonTitle }}
            </base-button>
          </slot>
        </template>
      </base-card>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
import { faArrowLeft, faTimes } from '@fortawesome/pro-regular-svg-icons';
import type { BaseValidation } from '@vuelidate/core';
import type { VCard } from 'vuetify/components';
import { VDialog } from 'vuetify/components';
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref,
  useSlots,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import debounce from '@/common/debounce';
import TheDialogLoader from '@/common/components/zvdzo/TheDialogLoader.vue';
import useDialogIsOpen from '@/common/composition/useDialogIsOpen';
import type {
  DialogEmits,
  DialogProps,
} from '@/common/components/zvdzo/BaseDialog.types';
import { DialogResolveStatus } from '@/common/components/zvdzo/BaseDialog.types';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';
import getMatomo from '@/common/composition/getMatomo';

type ResolveValue =
  | unknown
  | Promise<unknown>
  | (() => unknown)
  | (() => Promise<unknown>);

export type DialogButtonOptions = {
  title: string;
  value: ResolveValue;
  props?: Record<string, unknown>;
  on?: Record<string, () => unknown>;
};

const slots = useSlots();

const { t } = useI18n();

const focusDialogElement = (dialog: HTMLElement) => {
  const inputs = [
    ...dialog.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'input:not([type="hidden"]):not([style*="display: none"])' +
        ':not([style*="visibility: hidden"]):not([tabindex="-1"])' +
        ', select, textarea, [autofocus]',
    ),
  ].filter(
    input =>
      !input.closest('[style*="display: none"], [style*="visibility: hidden"]'),
  );

  if (inputs.length > 0) {
    inputs[0].focus();
    return;
  }

  const buttons = [
    ...dialog.querySelectorAll<HTMLButtonElement>('button.bg-primary'),
  ].filter(
    button =>
      !button.closest(
        '[style*="display: none"], [style*="visibility: hidden"]',
      ),
  );
  if (buttons.length === 1) {
    buttons[0].focus();
    return;
  }

  // focus dialog as a whole
  dialog.querySelector<HTMLElement>('div')?.focus();
};

const componentName = 'BaseDialog';

type Props = ActionableComponentProps &
  DialogProps & {
    width?: string | number;
    validation?: BaseValidation;
    fullscreen?: boolean;
    title?: string;
    content?: string;
    loading?: boolean;
    disableDialogLoader?: boolean;
    contentClass?: string;
    buttons?: DialogButtonOptions[];
    persistent?: boolean;
    cancelable?: boolean;
    actionsClass?: string | Record<string, boolean> | string[];
    trackName?: string;
  };
const props = withDefaults(defineProps<Props>(), {
  width: 500,
  contentClass: 'pt-4',
  cancelable: true,
  fullscreen: undefined,
});
const emit = defineEmits<DialogEmits>();

const internalIsOpen = useDialogIsOpen(props, emit);

const card = ref<typeof VCard>();
const vDialogRef = ref<typeof VDialog>();

const display = useDisplay();
const computedFullscreen = computed<boolean>(() => {
  if (props.fullscreen === undefined) {
    return display.xs.value;
  }
  return props.fullscreen;
});

const resolver = ref<((value?: unknown) => void) | void>();

const cancel = () => {
  if (!props.cancelable) {
    return;
  }
  internalIsOpen.value = false;
  if (resolver.value) {
    resolver.value();
  }
  emit('resolve');
};

const cancelWhenNotPersistent = (emitName?: 'click:outside') => {
  if (!props.persistent) {
    cancel();
  }
  if (emitName) {
    emit(emitName);
  }
};

const resolve = (value?: unknown) => {
  if (props.loading) {
    return;
  }
  if (props.validation?.$invalid) {
    props.validation.$touch();
    nextTick(() => {
      if (!card.value) {
        throw new Error('no card found');
      }
      const element = card.value.$el.querySelector('.text-error:first-of-type');
      element?.scrollIntoView();

      const input = element?.querySelector('input, textarea') as
        | HTMLInputElement
        | HTMLTextAreaElement
        | undefined;
      input?.select();
    });
    return;
  }

  if (value === undefined) {
    cancel();
    return;
  }

  const r = (resolveValue: unknown) => {
    if (resolveValue === DialogResolveStatus.stop) {
      return;
    }
    internalIsOpen.value = false;
    if (resolver.value) {
      resolver.value(resolveValue);
    }
    emit('resolve', resolveValue);
  };

  if (value instanceof Function) {
    const resolveValue = value();
    if (resolveValue instanceof Promise) {
      resolveValue.then(r);
      return;
    }
    r(resolveValue);
    return;
  }
  if (value instanceof Promise) {
    value.then(r);
    return;
  }
  r(value);
};

const open = () => {
  internalIsOpen.value = true;
  return new Promise(r => {
    resolver.value = (value?: unknown) => {
      r(value);
    };
  });
};

const setInternalFocus = debounce(() => {
  if (!vDialogRef.value) {
    // eslint-disable-next-line no-console
    console.warn('no dialog found');
    return;
  }
  if (!internalIsOpen.value) {
    return;
  }
  const dialog = vDialogRef.value.contentEl;
  if (!dialog) {
    return;
  }
  focusDialogElement(dialog as HTMLElement);
}, 500);

watch(
  () => internalIsOpen.value && !props.loading,
  value => {
    if (!value) {
      return;
    }
    setInternalFocus();
  },
  { immediate: true },
);

// Matomo tracking
enum trackState {
  Open = 'open',
  Close = 'close',
}
let trackDialog: (state: trackState, name?: string) => void | undefined;
onMounted(() => {
  const self = getCurrentInstance();
  trackDialog = (state, name) => {
    getMatomo(self)?.trackEvent('dialog', state, name || 'Unknown');
  };
});

watch(
  internalIsOpen,
  (value, prevValue) => {
    if (prevValue === undefined && !value) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    trackDialog &&
      trackDialog(
        internalIsOpen.value ? trackState.Open : trackState.Close,
        props.trackName || props.title || 'Unknown',
      );
  },
  { immediate: true },
);

defineExpose({ open, cancel });

defineSlots<{
  title(props: { title?: string }): void;
  default(props: {
    isOpen: boolean;
    resolve: (v: unknown) => void;
    cancel: () => void;
  }): void;
  actions(props: {
    isOpen: boolean;
    resolve: (v: unknown) => void;
    cancel: () => void;
  }): void;
}>();
</script>

<style lang="scss">
.base-dialog:focus {
  outline: solid 2px var(--v-primary-base);
}
.v-dialog > .v-overlay__content > .v-card,
.v-dialog > .v-overlay__content > .v-sheet {
  overflow-y: hidden;
}
</style>
